"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCheckOutline18, IconChevronExpandYOutline18, IconVaultOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMorphoVaultSearch, fetchVaultByAddress, MORPHO_CHAIN_NAMES, MORPHO_SUPPORTED_CHAIN_IDS, type MorphoVault } from "@/hooks/useMorphoVaultSearch";
import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { cn, formatAddress } from "@/lib/utils";
import { addRecentMorphoVault, type RecentMorphoVault } from "@/lib/utils/recentSelections";
import { formatApy, formatUsd } from "@/lib/utils/morphoFormatters";
import * as React from "react";
interface SelectedVaultInfo {
  address: string;
  name: string;
  assetSymbol: string;
  chainId: number;
  netApy: number;
}
function toSelectedInfo(vault: MorphoVault | RecentMorphoVault | SelectedVaultInfo): SelectedVaultInfo {
  return {
    address: vault.address,
    name: vault.name,
    assetSymbol: vault.assetSymbol,
    chainId: vault.chainId,
    netApy: vault.netApy
  };
}
interface MorphoVaultSelectorProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string | undefined;
  onChange: (value: string) => void;
  /** Filter to a specific chain ID */
  chainId?: number;
  /** When true, Popover uses modal mode (required inside dialogs to receive pointer events) */
  popoverModal?: boolean;
  /** When true, show built-in chain filter pills (used when chain-id field is hidden) */
  showChainFilter?: boolean;
  /** Callback when user selects a chain from the built-in filter */
  onChainIdChange?: (chainId: number | undefined) => void;
}
const EMPTY_CHAIN_IDS: number[] = [];

/** Type-guard: true when val is a positive number (valid chain ID) */
const isValidChainId = (val: unknown): val is number => typeof val === "number" && val > 0;
function MorphoVaultSelector(t0) {
  const $ = _c(76);
  const {
    label,
    description,
    headerRight,
    value,
    onChange,
    chainId,
    popoverModal: t1,
    showChainFilter: t2,
    onChainIdChange
  } = t0;
  const popoverModal = t1 === undefined ? false : t1;
  const showChainFilter = t2 === undefined ? false : t2;
  const listboxId = React.useId();
  const [containerEl, setContainerEl] = React.useState(null);
  const searchInputRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [selectedVault, setSelectedVault] = React.useState(null);
  let t3;
  if ($[0] !== chainId || $[1] !== showChainFilter) {
    t3 = () => showChainFilter && isValidChainId(chainId) ? chainId : undefined;
    $[0] = chainId;
    $[1] = showChainFilter;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [selectedFilterChainId, setSelectedFilterChainId] = React.useState(t3);
  let t4;
  let t5;
  if ($[3] !== chainId || $[4] !== showChainFilter) {
    t4 = () => {
      if (showChainFilter && isValidChainId(chainId)) {
        setSelectedFilterChainId(chainId);
      } else {
        if (showChainFilter) {
          setSelectedFilterChainId(undefined);
        }
      }
    };
    t5 = [chainId, showChainFilter];
    $[3] = chainId;
    $[4] = showChainFilter;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  React.useEffect(t4, t5);
  const effectiveChainId = showChainFilter ? selectedFilterChainId : chainId;
  const {
    data: chainInfoMap
  } = useChainInfoMap(showChainFilter ? MORPHO_SUPPORTED_CHAIN_IDS : EMPTY_CHAIN_IDS);
  let t6;
  if ($[7] !== effectiveChainId || $[8] !== open) {
    t6 = {
      chainId: effectiveChainId,
      open
    };
    $[7] = effectiveChainId;
    $[8] = open;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    setSearchQuery,
    isLoading,
    vaults,
    allVaults,
    recentVaults,
    refreshRecentVaults,
    searchQuery
  } = useMorphoVaultSearch(t6);
  const selectedAddressRef = React.useRef(null);
  let t7;
  if ($[10] !== selectedVault?.address) {
    t7 = () => {
      selectedAddressRef.current = selectedVault?.address.toLowerCase() ?? null;
    };
    $[10] = selectedVault?.address;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== selectedVault) {
    t8 = [selectedVault];
    $[12] = selectedVault;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  React.useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== allVaults || $[15] !== effectiveChainId || $[16] !== recentVaults || $[17] !== value) {
    t9 = () => {
      if (!value) {
        setSelectedVault(null);
        return;
      }
      if (selectedAddressRef.current === value.toLowerCase()) {
        return;
      }
      const found = allVaults.find(v => v.address.toLowerCase() === value.toLowerCase());
      if (found) {
        setSelectedVault(toSelectedInfo(found));
        return;
      }
      const recent = recentVaults.find(v_0 => v_0.address.toLowerCase() === value.toLowerCase());
      if (recent) {
        setSelectedVault(toSelectedInfo(recent));
        return;
      }
      if (/^0x[a-fA-F0-9]{40}$/.test(value)) {
        let cancelled;
        cancelled = false;
        setSelectedVault(null);
        fetchVaultByAddress(value, effectiveChainId).then(vault => {
          if (cancelled) {
            return;
          }
          if (vault) {
            setSelectedVault(toSelectedInfo(vault));
          } else {
            selectedAddressRef.current = value.toLowerCase();
          }
        }).catch(_temp);
        return () => {
          cancelled = true;
        };
      }
      setSelectedVault(null);
    };
    t10 = [value, allVaults, recentVaults, effectiveChainId];
    $[14] = allVaults;
    $[15] = effectiveChainId;
    $[16] = recentVaults;
    $[17] = value;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  React.useEffect(t9, t10);
  let t11;
  if ($[20] !== onChainIdChange || $[21] !== onChange || $[22] !== refreshRecentVaults || $[23] !== showChainFilter) {
    t11 = vault_0 => {
      const info = toSelectedInfo(vault_0);
      setSelectedVault(info);
      onChange(vault_0.address);
      setOpen(false);
      addRecentMorphoVault(info);
      refreshRecentVaults();
      if (onChainIdChange) {
        onChainIdChange(vault_0.chainId);
      }
      if (showChainFilter) {
        setSelectedFilterChainId(vault_0.chainId);
      }
    };
    $[20] = onChainIdChange;
    $[21] = onChange;
    $[22] = refreshRecentVaults;
    $[23] = showChainFilter;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const handleSelect = t11;
  let t12;
  if ($[25] !== effectiveChainId || $[26] !== handleSelect || $[27] !== value) {
    t12 = (vault_1, keyPrefix) => <CommandItem key={`${keyPrefix}-${vault_1.chainId}-${vault_1.address}`} value={`${vault_1.chainId}-${vault_1.address}`} onSelect={() => handleSelect(vault_1)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800"><div className="flex w-full cursor-pointer items-center gap-2"><IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", value?.toLowerCase() === vault_1.address.toLowerCase() ? "opacity-100" : "opacity-0")} /><IconVaultOutline18 className="text-ds-text-muted h-4 w-4 flex-shrink-0" /><div className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-medium">{vault_1.name}</span><span className="text-ds-text-muted text-xs">{vault_1.assetSymbol}{" \xB7 "}{formatApy(vault_1.netApy)} APY{"tvlUsd" in vault_1 && vault_1.tvlUsd > 0 && <>{" \xB7 "}{formatUsd(vault_1.tvlUsd)} TVL</>}{!effectiveChainId && " \xB7 " + (MORPHO_CHAIN_NAMES[vault_1.chainId] ?? `Chain ${vault_1.chainId}`)}</span></div><span className="text-ds-text-disabled flex-shrink-0 font-mono text-xs">{formatAddress(vault_1.address)}</span></div></CommandItem>;
    $[25] = effectiveChainId;
    $[26] = handleSelect;
    $[27] = value;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const renderVaultItem = t12;
  let t13;
  if ($[29] !== headerRight || $[30] !== label) {
    t13 = label && <div className="flex items-center justify-between"><label className="field-label block">{label}</label>{headerRight && <div className="flex items-center gap-3">{headerRight}</div>}</div>;
    $[29] = headerRight;
    $[30] = label;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== description) {
    t14 = description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>;
    $[32] = description;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== selectedVault || $[35] !== value) {
    t15 = selectedVault ? <span className="flex items-center gap-2"><IconVaultOutline18 className="text-ds-text-muted h-4 w-4 flex-shrink-0" /><span className="text-ds-text-primary truncate">{selectedVault.name}</span><span className="text-ds-text-muted flex-shrink-0 text-xs">{selectedVault.assetSymbol} · {formatApy(selectedVault.netApy)}</span></span> : value ? <span className="flex items-center gap-2"><IconVaultOutline18 className="text-ds-text-muted h-4 w-4 flex-shrink-0" /><span className="text-ds-text-primary truncate font-mono">{formatAddress(value)}</span></span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">Select Morpho vault...</span>;
    $[34] = selectedVault;
    $[35] = value;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <IconChevronExpandYOutline18 className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== listboxId || $[39] !== open || $[40] !== t15) {
    t17 = <PopoverTrigger asChild={true}><button type="button" role="combobox" aria-controls={listboxId} aria-expanded={open} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">{t15}{t16}</button></PopoverTrigger>;
    $[38] = listboxId;
    $[39] = open;
    $[40] = t15;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const t18 = popoverModal ? containerEl ?? undefined : undefined;
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = e => {
      e.preventDefault();
      searchInputRef.current?.focus({
        preventScroll: true
      });
    };
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== chainInfoMap || $[44] !== onChainIdChange || $[45] !== selectedFilterChainId || $[46] !== showChainFilter) {
    t20 = showChainFilter && <div className="border-ds-border-default flex items-center gap-1 border-b px-2 py-1.5"><button type="button" onClick={() => {
        setSelectedFilterChainId(undefined);
        onChainIdChange?.(undefined);
      }} className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${!selectedFilterChainId ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" : "text-ds-text-muted hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800"}`}>All</button>{MORPHO_SUPPORTED_CHAIN_IDS.map(cId => {
        const info_0 = chainInfoMap.get(cId);
        const name = info_0?.name ?? MORPHO_CHAIN_NAMES[cId] ?? `Chain ${cId}`;
        return <button key={`filter-${cId}`} type="button" title={name} onClick={() => {
          setSelectedFilterChainId(cId);
          if (onChainIdChange) {
            onChainIdChange(cId);
          }
        }} className={`flex flex-shrink-0 items-center gap-1 rounded-full p-1 pr-2 transition-colors ${selectedFilterChainId === cId ? "bg-blue-100 ring-1 ring-blue-400 dark:bg-blue-900/40 dark:ring-blue-500" : "hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800"}`}>{info_0?.iconUrl ? <img src={info_0.iconUrl} alt={name} className="h-4 w-4 rounded-full" /> : <div className="bg-ds-neutral-400 dark:bg-ds-neutral-600 h-4 w-4 rounded-full" />}<span className="text-xs">{MORPHO_CHAIN_NAMES[cId] ?? name}</span></button>;
      })}</div>;
    $[43] = chainInfoMap;
    $[44] = onChainIdChange;
    $[45] = selectedFilterChainId;
    $[46] = showChainFilter;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== searchQuery || $[49] !== setSearchQuery) {
    t21 = <CommandInput ref={searchInputRef} placeholder="Search vaults..." value={searchQuery} onValueChange={setSearchQuery} />;
    $[48] = searchQuery;
    $[49] = setSearchQuery;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== isLoading || $[52] !== recentVaults || $[53] !== renderVaultItem || $[54] !== searchQuery || $[55] !== vaults) {
    t22 = isLoading ? <div className="body-sm py-6 text-center">Loading...</div> : <><CommandEmpty>No vaults found.</CommandEmpty>{recentVaults.length > 0 && !searchQuery && <CommandGroup heading="Recently Used">{recentVaults.map(vault_2 => renderVaultItem(vault_2, "recent"))}</CommandGroup>}<CommandGroup heading={recentVaults.length > 0 && !searchQuery ? "All Vaults" : undefined}>{vaults.map(vault_3 => renderVaultItem(vault_3, "all"))}</CommandGroup></>;
    $[51] = isLoading;
    $[52] = recentVaults;
    $[53] = renderVaultItem;
    $[54] = searchQuery;
    $[55] = vaults;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== listboxId || $[58] !== t22) {
    t23 = <CommandList id={listboxId} className="max-h-64">{t22}</CommandList>;
    $[57] = listboxId;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t20 || $[61] !== t21 || $[62] !== t23) {
    t24 = <Command shouldFilter={false}>{t20}{t21}{t23}</Command>;
    $[60] = t20;
    $[61] = t21;
    $[62] = t23;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== t18 || $[65] !== t24) {
    t25 = <PopoverContent className="bg-ds-surface-card w-[var(--radix-popover-trigger-width)] p-0" align="start" container={t18} onOpenAutoFocus={t19}>{t24}</PopoverContent>;
    $[64] = t18;
    $[65] = t24;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== open || $[68] !== popoverModal || $[69] !== t17 || $[70] !== t25) {
    t26 = <Popover open={open} onOpenChange={setOpen} modal={popoverModal}>{t17}{t25}</Popover>;
    $[67] = open;
    $[68] = popoverModal;
    $[69] = t17;
    $[70] = t25;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t13 || $[73] !== t14 || $[74] !== t26) {
    t27 = <div ref={setContainerEl} className="space-y-2">{t13}{t14}{t26}</div>;
    $[72] = t13;
    $[73] = t14;
    $[74] = t26;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  return t27;
}
function _temp() {}
export default React.memo(MorphoVaultSelector);